import api from './api';

export interface CMSPage {
  id: number;
  slug: string;
  title_en: string;
  title_tr: string;
  content_en: string;
  content_tr: string;
  page_type: string;
  is_published: boolean;
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at?: string;
  meta_description_en?: string;
  meta_description_tr?: string;
}

export interface CMSPageCreate {
  slug: string;
  title_en: string;
  title_tr: string;
  content_en: string;
  content_tr: string;
  page_type: string;
  meta_description_en?: string;
  meta_description_tr?: string;
  is_published?: boolean;
}

export interface CMSPageUpdate {
  title_en?: string;
  title_tr?: string;
  content_en?: string;
  content_tr?: string;
  page_type?: string;
  meta_description_en?: string;
  meta_description_tr?: string;
  is_published?: boolean;
}

export interface BlogPost {
  id: number;
  slug: string;
  title_en: string;
  title_tr: string;
  content_en: string;
  content_tr: string;
  excerpt_en?: string;
  excerpt_tr?: string;
  cover_image?: string;
  author: string;
  is_published: boolean;
  published_at?: string;
  created_at: string;
  updated_at?: string;
  tags?: string;
  view_count: number;
}

export interface BlogPostCreate {
  slug: string;
  title_en: string;
  title_tr: string;
  content_en: string;
  content_tr: string;
  excerpt_en?: string;
  excerpt_tr?: string;
  cover_image?: string;
  tags?: string;
  is_published?: boolean;
}

export interface BlogPostUpdate {
  slug?: string;
  title_en?: string;
  title_tr?: string;
  content_en?: string;
  content_tr?: string;
  excerpt_en?: string;
  excerpt_tr?: string;
  cover_image?: string;
  tags?: string;
  is_published?: boolean;
}

export interface FAQItem {
  id: number;
  question_en: string;
  question_tr: string;
  answer_en: string;
  answer_tr: string;
  category: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at?: string;
}

export interface FAQItemCreate {
  question_en: string;
  question_tr: string;
  answer_en: string;
  answer_tr: string;
  category: string;
  display_order?: number;
  is_published?: boolean;
}

export interface FAQItemUpdate {
  question_en?: string;
  question_tr?: string;
  answer_en?: string;
  answer_tr?: string;
  category?: string;
  display_order?: number;
  is_published?: boolean;
}

export const cmsService = {
  // CMS Pages
  async getAllPages(includeUnpublished = false): Promise<CMSPage[]> {
    const response = await api.get<CMSPage[]>(`/cms/pages?include_unpublished=${includeUnpublished}`);
    return response.data;
  },

  async getPageBySlug(slug: string): Promise<CMSPage> {
    const response = await api.get<CMSPage>(`/cms/pages/${slug}`);
    return response.data;
  },

  async createPage(data: CMSPageCreate): Promise<CMSPage> {
    const response = await api.post<CMSPage>('/cms/pages', data);
    return response.data;
  },

  async updatePage(pageId: number, data: CMSPageUpdate): Promise<CMSPage> {
    const response = await api.put<CMSPage>(`/cms/pages/${pageId}`, data);
    return response.data;
  },

  async deletePage(pageId: number): Promise<void> {
    await api.delete(`/cms/pages/${pageId}`);
  },

  // Blog Posts
  async getAllBlogPosts(includeUnpublished = false): Promise<BlogPost[]> {
    const response = await api.get<BlogPost[]>(`/cms/blog?include_unpublished=${includeUnpublished}`);
    return response.data;
  },

  async getBlogPostBySlug(slug: string): Promise<BlogPost> {
    const response = await api.get<BlogPost>(`/cms/blog/${slug}`);
    return response.data;
  },

  async createBlogPost(data: BlogPostCreate): Promise<BlogPost> {
    const response = await api.post<BlogPost>('/cms/blog', data);
    return response.data;
  },

  async updateBlogPost(postId: number, data: BlogPostUpdate): Promise<BlogPost> {
    const response = await api.put<BlogPost>(`/cms/blog/${postId}`, data);
    return response.data;
  },

  async deleteBlogPost(postId: number): Promise<void> {
    await api.delete(`/cms/blog/${postId}`);
  },

  // FAQ Items
  async getAllFAQItems(includeUnpublished = false, category?: string): Promise<FAQItem[]> {
    let url = `/cms/faq?include_unpublished=${includeUnpublished}`;
    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }
    const response = await api.get<FAQItem[]>(url);
    return response.data;
  },

  async getFAQItemById(faqId: number): Promise<FAQItem> {
    const response = await api.get<FAQItem>(`/cms/faq/${faqId}`);
    return response.data;
  },

  async createFAQItem(data: FAQItemCreate): Promise<FAQItem> {
    const response = await api.post<FAQItem>('/cms/faq', data);
    return response.data;
  },

  async updateFAQItem(faqId: number, data: FAQItemUpdate): Promise<FAQItem> {
    const response = await api.put<FAQItem>(`/cms/faq/${faqId}`, data);
    return response.data;
  },

  async deleteFAQItem(faqId: number): Promise<void> {
    await api.delete(`/cms/faq/${faqId}`);
  },
};

